import { z } from 'zod';
/**
 * Der Bereich Haus: die Rechnungen der Energie- und Wasserversorgung und
 * alles, was sich daraus über Verbrauch und Kosten des Hauses sagen lässt.
 *
 * Der Versorger stellt zwei Arten von Rechnungen: eine für den Strom und eine
 * für Wasser, Abwasser und Kehricht zusammen. Die zweite trägt also drei
 * Sparten auf einem Beleg – mit eigener Ablesung, eigenen Positionen und
 * eigenem Total je Sparte, aber einem gemeinsamen Akontoabzug und einem
 * gemeinsamen Rechnungsbetrag.
 *
 * Deshalb ist die Rechnung hier der Beleg und die Sparte die Auswertungseinheit:
 * Wer nur das Abwasser anschauen will, bekommt dessen Zeilen; wer wissen will,
 * was überwiesen wurde, bekommt die Rechnung. Beides aus derselben Zeile zu
 * lesen ginge nicht, ohne eine der beiden Fragen falsch zu beantworten.
 *
 * Beträge stehen wie überall in der App als ganze Rappen in `…Cents`. Mengen
 * (kWh, m³) sind ganze Zahlen – die Rechnung weist sie nie feiner aus. Ansätze
 * haben zwei Nachkommastellen ("18.50 Rp./kWh") und liegen deshalb als
 * Hundertstel vor: 18.50 → 1850. Prozentsätze stehen als Basispunkte, damit
 * 8.10 % (810) und 2.60 % (260) exakt bleiben.
 */
// ------------------------------------------------------------------ Sparten
export const DIVISIONS = ['strom', 'wasser', 'abwasser', 'kehricht'];
export const DIVISION_LABELS = {
    strom: 'Strom',
    wasser: 'Wasser',
    abwasser: 'Abwasser',
    kehricht: 'Kehricht',
};
/**
 * Worin eine Sparte gemessen wird – oder null, wenn sie gar nichts misst.
 *
 * Der Kehricht wird pauschal verrechnet: eine Gebühr je Wohnung und Jahr, ohne
 * Menge. Ein erfundener Verbrauch von 1 wäre keine Angabe, sondern eine
 * Behauptung, und ein Preis „je Stück" beantwortete keine Frage.
 */
export const DIVISION_UNITS = {
    strom: 'kWh',
    wasser: 'm³',
    abwasser: 'm³',
    kehricht: null,
};
/**
 * Wie die Sparte auf dem Beleg heisst.
 *
 * Der Versorger überschreibt die Blöcke mit „Elektrizität" beziehungsweise
 * „Wasser", „Abwasser" und „Kehricht"; die Summenzeilen darunter lauten
 * „Total Wasser" und so fort. Beides führt hierher.
 */
export const DIVISION_KEYWORDS = {
    strom: ['elektrizität', 'elektrizitaet', 'strom'],
    wasser: ['wasser'],
    abwasser: ['abwasser'],
    kehricht: ['kehricht'],
};
/**
 * Die Blöcke, in die der Versorger den Strom teilt.
 *
 * Nur beim Strom: Wasser, Abwasser und Kehricht kennen keine solche
 * Unterteilung, dort ist die Sparte selbst schon der Block. An ihnen lässt
 * sich ablesen, woher eine Verteuerung kommt – steigt der Netzanteil, hilft
 * kein Sparen an der Energie.
 *
 * Die Liste ist die einzige Stelle, an der steht, welche Blöcke es gibt:
 * Formular, Diagramm, Aufteilung und CSV leiten ihre Spalten daraus ab. Der
 * Versorger hat auf 2026 die „Messung" aus der Netznutzung herausgelöst – ein
 * vierter Block, der vorher nicht existierte. Wer solche Blöcke an mehreren
 * Stellen aufzählt, verliert bei jedem neuen einen Teil der Rechnung, ohne
 * dass es auffällt: Die Summe bleibt plausibel, sie ist nur zu klein.
 *
 * Die Reihenfolge ist die der Rechnung – so stehen die Blöcke im Formular,
 * im Diagramm und in der CSV so untereinander wie auf dem Papier daneben.
 */
export const COST_GROUPS = ['energie', 'netznutzung', 'abgaben', 'messung'];
export const COST_GROUP_LABELS = {
    energie: 'Energie',
    netznutzung: 'Netznutzung',
    abgaben: 'Abgaben & Förderbeiträge',
    messung: 'Messung',
};
/** Kurzform für enge Stellen – Diagrammlegende, Tabellenkopf. */
export const COST_GROUP_SHORT = {
    energie: 'Energie',
    netznutzung: 'Netz',
    abgaben: 'Abgaben',
    messung: 'Messung',
};
/** Baut die Blockbeträge aus dem, was auf einer Sparte steht. */
export function costGroupCents(groups) {
    const betraege = Object.fromEntries(COST_GROUPS.map((group) => [group, 0]));
    for (const entry of groups)
        betraege[entry.group] += entry.amountCents;
    return betraege;
}
export const BILL_KINDS = ['abrechnung', 'akonto'];
export const BILL_KIND_LABELS = {
    abrechnung: 'Abrechnung',
    akonto: 'Akontorechnung',
};
/**
 * Der Tarif einer Ablesung. `einzel` steht für alles, was nur einen Zähler
 * hat – Wasser kennt keinen Hoch- und Niedertarif.
 */
export const TARIFFS = ['hoch', 'nieder', 'einzel'];
export const TARIFF_LABELS = {
    hoch: 'Hochtarif',
    nieder: 'Niedertarif',
    einzel: 'Verbrauch',
};
/**
 * Wie eine Ablesung heissen soll: so wie auf der Rechnung, sonst nach Tarif.
 *
 * Der Versorger nennt dieselben zwei Tarife seit 2026 „Tagtarif" und
 * „Nachttarif" statt „Hochtarif" und „Niedertarif". Ausgewertet werden sie
 * gleich – angezeigt wird, was auf dem Papier steht, sonst behauptet die App
 * über den Beleg etwas, das sich dort nicht nachlesen lässt.
 */
export function readingLabel(reading) {
    return reading.label.trim() || TARIFF_LABELS[reading.tariff];
}
/**
 * Der Tarifname allein, für enge Stellen wie Formularbeschriftungen.
 *
 * „Wirkstrom Tagtarif" → „Tagtarif". Gesucht wird das letzte Wort, das auf
 * „tarif" endet; findet sich keines, bleibt es beim eigenen Namen. So heisst
 * das Feld wie die Zeile auf dem Papier, ohne dass die Liste der möglichen
 * Namen hier ein zweites Mal gepflegt werden müsste.
 */
export function tariffName(label, tariff) {
    const wort = label
        .trim()
        .split(/\s+/)
        .filter((teil) => /tarif$/i.test(teil))
        .at(-1);
    return wort ? wort.charAt(0).toUpperCase() + wort.slice(1) : TARIFF_LABELS[tariff];
}
// ------------------------------------------------------------------ Schemas
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format JJJJ-MM-TT');
/**
 * Eine Zeile aus der Betragsermittlung, so wie sie auf der Rechnung steht.
 *
 * Bewusst als Rohabbild und nicht als gerechnetes Ergebnis: Was der Versorger
 * verrechnet hat, soll später nachlesbar bleiben, auch wenn sich die Auswertung
 * einmal ändert. Die Kennzahlen entstehen daraus, nie umgekehrt.
 */
export const billPositionSchema = z.object({
    /** Nur beim Strom gesetzt – sonst ist die Sparte selbst schon der Block. */
    group: z.enum(COST_GROUPS).nullable().default(null),
    label: z.string().trim().min(1).max(120),
    /** Menge in der Einheit der Zeile – 3286 kWh, 133 m², 1 Grundgebühr. */
    quantity: z.number().min(0).max(10_000_000),
    unit: z.string().trim().max(12).default(''),
    /** Ansatz mal 100: „18.50 Rp." → 1850. null, wenn die Zeile keinen ausweist. */
    rateHundredths: z.number().int().min(0).max(100_000_000).nullable().default(null),
    /** 'Rp.' oder 'Fr.' – ohne die Einheit ist der Ansatz um Faktor 100 daneben. */
    rateUnit: z.string().trim().max(6).nullable().default(null),
    /** „6 Mt." beim Grundpreis. null bei mengenabhängigen Zeilen. */
    durationMonths: z.number().int().min(0).max(120).nullable().default(null),
    /** MWST-Satz in Basispunkten: 8.10 % → 810. */
    vatBasisPoints: z.number().int().min(0).max(10_000).default(0),
    netCents: z.number().int().min(0).max(100_000_000),
    grossCents: z.number().int().min(0).max(100_000_000),
});
/**
 * Eine Zählerablesung.
 *
 * Steht getrennt von den Beträgen, obwohl sich der eine aus dem anderen
 * ergibt: Der Zählerstand ist die einzige Zahl, die man am Gerät selbst
 * nachprüfen kann. Wer wissen will, ob eine Rechnung stimmt, schaut dort nach
 * – und nicht auf eine Differenz, die die App gebildet hat.
 */
export const meterReadingSchema = z.object({
    tariff: z.enum(TARIFFS).default('einzel'),
    /** Wie die Zeile auf der Rechnung heisst: „Wirkstrom Hochtarif", „Wasserverbrauch". */
    label: z.string().trim().max(80).default(''),
    unit: z.string().trim().max(12).default(''),
    /** Stand alt / Stand neu, wie auf der Rechnung. */
    startValue: z.number().int().min(0).max(100_000_000).nullable().default(null),
    endValue: z.number().int().min(0).max(100_000_000).nullable().default(null),
    /** Ausgewiesene Menge. Kann von der Differenz abweichen (Zählerwechsel). */
    quantity: z.number().int().min(0).max(10_000_000),
});
/** Eine der drei Stromsummen – Energie, Netznutzung, gesetzliche Abgaben. */
export const costGroupTotalSchema = z.object({
    group: z.enum(COST_GROUPS),
    amountCents: z.number().int().min(0).max(100_000_000),
});
/**
 * Eine Sparte auf einer Rechnung – der Teil, um den es beim Auswerten geht.
 */
export const billSectionSchema = z.object({
    division: z.enum(DIVISIONS),
    /** Total dieser Sparte inkl. MWST, wie es auf der ersten Seite steht. */
    amountCents: z.number().int().min(0).max(100_000_000),
    /** Nur beim Strom gefüllt. */
    groups: z.array(costGroupTotalSchema).max(6).default([]),
    readings: z.array(meterReadingSchema).max(6).default([]),
    positions: z.array(billPositionSchema).max(40).default([]),
    meterPoint: z.string().trim().max(60).nullable().default(null),
    meterNumber: z.string().trim().max(40).nullable().default(null),
});
/**
 * Was aus einer Rechnung übernommen wird.
 *
 * Dasselbe Schema für den Import und für die Eingabe von Hand: Die Vorschau
 * nach dem Auslesen ist ein ausgefülltes Formular, kein Bericht. Was der
 * Automat falsch gelesen hat, wird an Ort und Stelle korrigiert, statt später
 * in einem zweiten Bildschirm.
 */
export const billInputSchema = z
    .object({
    kind: z.enum(BILL_KINDS),
    /** Die Nummer auf der Rechnung – sie macht jede Rechnung unverwechselbar. */
    invoiceNumber: z.string().trim().min(1).max(40),
    invoiceDate: isoDate,
    periodStart: isoDate,
    periodEnd: isoDate,
    customerNumber: z.string().trim().max(40).nullable().default(null),
    sections: z.array(billSectionSchema).min(1).max(6),
    /**
     * Zwischentotal – die Kosten der Periode, vor dem Akontoabzug.
     *
     * Das ist die Zahl, mit der gerechnet wird. Der Rechnungsbetrag darunter
     * sagt nur, wie viel davon noch offen war; wer den Preis daraus ableitet,
     * misst seine Abschlagszahlungen und nicht seinen Verbrauch.
     */
    subtotalCents: z.number().int().min(0).max(100_000_000),
    /** Bereits bezahlte Akontobeträge, positiv. 0 wenn keine abgezogen wurden. */
    prepaidCents: z.number().int().min(0).max(100_000_000).default(0),
    /** Rechnungsbetrag inkl. MWST – was tatsächlich zu überweisen war. */
    totalCents: z.number().int().min(0).max(100_000_000),
    vatCents: z.number().int().min(0).max(100_000_000).default(0),
    /** Das abgelegte PDF in den Dokumenten, sofern eines mitkam. */
    documentId: z.string().trim().max(60).nullable().default(null),
    /** Dateiname des importierten PDFs, damit die Herkunft nachvollziehbar ist. */
    sourceFile: z.string().trim().max(200).nullable().default(null),
    note: z.string().trim().max(500).default(''),
})
    .refine((bill) => bill.periodEnd >= bill.periodStart, {
    message: 'Das Ende der Periode liegt vor ihrem Anfang',
    path: ['periodEnd'],
});
/** Welche Sparten eine Rechnung überhaupt enthält, in fester Reihenfolge. */
export function divisionsOf(bill) {
    const vorhanden = new Set(bill.sections.map((section) => section.division));
    return DIVISIONS.filter((division) => vorhanden.has(division));
}
/** „Strom" oder „Wasser · Abwasser · Kehricht" – der Betreff einer Rechnung. */
export function billSubject(bill) {
    const divisions = divisionsOf(bill);
    if (divisions.length === 0)
        return 'Rechnung';
    return divisions.map((division) => DIVISION_LABELS[division]).join(' · ');
}
// --------------------------------------------------------------- Kennzahlen
/** Tage einer Periode, beide Enden mitgezählt: 1.–31.1. sind 31 Tage. */
export function periodDays(periodStart, periodEnd) {
    const start = Date.parse(`${periodStart}T00:00:00Z`);
    const end = Date.parse(`${periodEnd}T00:00:00Z`);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
        return 0;
    return Math.round((end - start) / 86_400_000) + 1;
}
/**
 * Das Jahr, dem eine Rechnung zugeschlagen wird: das der Periodenmitte.
 *
 * Nicht das Rechnungsdatum – die Abrechnung für das zweite Halbjahr 2024
 * kommt im Januar 2025 und gehört trotzdem zu 2024. Und nicht der Anfang der
 * Periode, weil eine über den Jahreswechsel laufende Periode sonst allein am
 * ersten Tag hinge.
 */
export function billYear(bill) {
    const start = Date.parse(`${bill.periodStart}T00:00:00Z`);
    const end = Date.parse(`${bill.periodEnd}T00:00:00Z`);
    if (!Number.isFinite(start) || !Number.isFinite(end))
        return 0;
    return new Date((start + end) / 2).getUTCFullYear();
}
/** Rechnet eine Sparte einer Rechnung durch. */
export function computeDivisionEntry(bill, section) {
    const days = periodDays(bill.periodStart, bill.periodEnd);
    const { high, low, total } = sectionQuantity(section);
    const costCents = section.amountCents;
    return {
        id: `${bill.id}:${section.division}`,
        bill,
        section,
        division: section.division,
        days,
        quantity: total,
        unit: DIVISION_UNITS[section.division],
        highQuantity: high,
        lowQuantity: low,
        costCents,
        groupCents: section.division === 'strom' ? costGroupCents(section.groups) : null,
        pricePerUnitHundredths: total && total > 0 ? Math.round((costCents * 100) / total) : null,
        quantityPerDay: total !== null && days > 0 ? total / days : null,
        costPerDayCents: days > 0 ? Math.round(costCents / days) : 0,
        annualQuantity: total !== null && days > 0 ? Math.round((total * 365) / days) : null,
        annualCostCents: days > 0 ? Math.round((costCents * 365) / days) : 0,
    };
}
/**
 * Verbrauch einer Sparte.
 *
 * Zuerst aus den Zählerablesungen, weil dort der Verbrauch selbst steht. Fehlt
 * die Ablesung – bei Akontorechnungen gibt es keine –, wird ersatzweise die
 * Menge der mengenabhängigen Positionen genommen. Wo gar nichts gemessen wird,
 * bleibt es bei null: Der Kehricht hat keinen Verbrauch, und eine 0 wäre eine
 * Behauptung.
 */
function sectionQuantity(section) {
    if (DIVISION_UNITS[section.division] === null)
        return { high: null, low: null, total: null };
    let high = null;
    let low = null;
    let einzel = null;
    for (const reading of section.readings) {
        if (reading.tariff === 'hoch')
            high = (high ?? 0) + reading.quantity;
        else if (reading.tariff === 'nieder')
            low = (low ?? 0) + reading.quantity;
        else
            einzel = (einzel ?? 0) + reading.quantity;
    }
    if (high !== null || low !== null || einzel !== null) {
        return { high, low, total: (high ?? 0) + (low ?? 0) + (einzel ?? 0) };
    }
    // Ersatzweise aus den Positionen: Nur Zeilen in der Einheit der Sparte
    // zählen – der Regenabwasser-Ansatz je Quadratmeter ist kein Verbrauch.
    //
    // Und nur die Zeilen eines einzigen Blocks. Dieselben Kilowattstunden
    // stehen beim Strom in jedem Block noch einmal: Der Netzzuschlag rechnet
    // mit ihnen wie die Energielieferung, und die gesetzlichen Abgaben bringen
    // sie gleich fünfmal mit. Über alle Blöcke summiert kommt ein Vielfaches
    // des Verbrauchs heraus – 5'220 kWh werden so zu 36'540. Gezählt wird
    // deshalb der Block, der die gelieferte Menge ausweist, und sonst keiner.
    const unit = DIVISION_UNITS[section.division];
    const passend = section.positions.filter((position) => position.unit === unit);
    const block = COST_GROUPS.find((group) => passend.some((position) => position.group === group)) ?? null;
    const zaehlbar = block === null
        ? passend.filter((position) => position.group === null)
        : passend.filter((position) => position.group === block);
    for (const position of zaehlbar) {
        const label = position.label.toLowerCase();
        if (label.includes('hochtarif') || label.includes('tagtarif')) {
            high = (high ?? 0) + Math.round(position.quantity);
        }
        else if (label.includes('niedertarif') || label.includes('nachttarif')) {
            low = (low ?? 0) + Math.round(position.quantity);
        }
        else {
            einzel = (einzel ?? 0) + Math.round(position.quantity);
        }
    }
    if (high === null && low === null && einzel === null)
        return { high: null, low: null, total: null };
    return { high, low, total: (high ?? 0) + (low ?? 0) + (einzel ?? 0) };
}
/**
 * Vergleicht zwei Werte.
 *
 * Unter einem halben Prozent gilt als „gleich": Zwei Perioden sind nie exakt
 * gleich lang, und ein Pfeil, der bei 0.2 % Unterschied nach oben zeigt,
 * behauptet mehr als er weiss.
 */
export function trendBetween(current, previous) {
    if (current === null || previous === null || previous === 0) {
        return { ratio: null, direction: 'gleich', delta: null };
    }
    const delta = current - previous;
    const ratio = delta / Math.abs(previous);
    return {
        ratio,
        delta,
        direction: Math.abs(ratio) < 0.005 ? 'gleich' : ratio > 0 ? 'steigend' : 'sinkend',
    };
}
export const EMPTY_TREND = { ratio: null, direction: 'gleich', delta: null };
/**
 * Sucht zum Eintrag den entsprechenden des Vorjahres.
 *
 * Verglichen wird dieselbe Sparte über den Anfang der Periode, mit einem
 * Fenster von 45 Tagen: Die Ablesung findet nicht jedes Jahr am selben Tag
 * statt, und ein Vergleich, der bei drei Wochen Abweichung aufgibt, ist
 * keiner. Ein Winterhalbjahr wird so nie versehentlich mit einem
 * Sommerhalbjahr verglichen.
 */
export function findPreviousYearEntry(entries, entry) {
    const target = Date.parse(`${entry.bill.periodStart}T00:00:00Z`);
    if (!Number.isFinite(target))
        return null;
    const wanted = target - 365 * 86_400_000;
    let best = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const candidate of entries) {
        if (candidate.id === entry.id)
            continue;
        if (candidate.division !== entry.division)
            continue;
        if (candidate.bill.kind !== entry.bill.kind)
            continue;
        const start = Date.parse(`${candidate.bill.periodStart}T00:00:00Z`);
        if (!Number.isFinite(start))
            continue;
        const distance = Math.abs(start - wanted);
        if (distance <= 45 * 86_400_000 && distance < bestDistance) {
            best = candidate;
            bestDistance = distance;
        }
    }
    return best;
}
export const EMPTY_SUMMARY = {
    entryCount: 0,
    billCount: 0,
    days: 0,
    totalCostCents: 0,
    costPerDayCents: null,
    annualCostCents: null,
    perDivision: [],
    unit: null,
    quantity: null,
    highQuantity: null,
    lowQuantity: null,
    pricePerUnitHundredths: null,
    quantityPerDay: null,
    groupCents: null,
};
/**
 * Fasst Spartenabschnitte zu einer Periode zusammen.
 *
 * Die abgedeckten Tage sind die Spanne vom frühesten Anfang bis zum spätesten
 * Ende und nicht die Summe der Perioden: Strom und Wasser laufen im selben
 * Jahr nebeneinander her: Ihre Tage zu addieren ergäbe ein doppelt so langes
 * Jahr und damit halb so hohe Kosten pro Tag.
 *
 * Der Ø Preis wird aus den Summen gebildet und nicht als Mittel der einzelnen
 * Preise: Ein Halbjahr mit wenig Verbrauch soll den Jahresschnitt nicht gleich
 * stark ziehen wie eines mit viel.
 */
export function summarize(entries) {
    if (entries.length === 0)
        return { ...EMPTY_SUMMARY };
    const einheiten = new Set(entries.map((entry) => entry.unit));
    const sparten = new Set(entries.map((entry) => entry.division));
    const einheitlich = einheiten.size === 1 ? ([...einheiten][0] ?? null) : null;
    const nurStrom = sparten.size === 1 && sparten.has('strom');
    let totalCostCents = 0;
    let high = null;
    let low = null;
    let menge = null;
    const groupCents = nurStrom ? costGroupCents([]) : null;
    let von = Number.POSITIVE_INFINITY;
    let bis = Number.NEGATIVE_INFINITY;
    const proSparte = new Map();
    const rechnungen = new Set();
    for (const entry of entries) {
        totalCostCents += entry.costCents;
        const bisher = proSparte.get(entry.division) ?? { costCents: 0, quantity: null };
        proSparte.set(entry.division, {
            costCents: bisher.costCents + entry.costCents,
            quantity: entry.quantity === null ? bisher.quantity : (bisher.quantity ?? 0) + entry.quantity,
        });
        rechnungen.add(entry.bill.id);
        if (einheitlich !== null) {
            if (entry.quantity !== null)
                menge = (menge ?? 0) + entry.quantity;
            if (entry.highQuantity !== null)
                high = (high ?? 0) + entry.highQuantity;
            if (entry.lowQuantity !== null)
                low = (low ?? 0) + entry.lowQuantity;
        }
        if (groupCents && entry.groupCents) {
            for (const group of COST_GROUPS)
                groupCents[group] += entry.groupCents[group];
        }
        const start = Date.parse(`${entry.bill.periodStart}T00:00:00Z`);
        const end = Date.parse(`${entry.bill.periodEnd}T00:00:00Z`);
        if (Number.isFinite(start))
            von = Math.min(von, start);
        if (Number.isFinite(end))
            bis = Math.max(bis, end);
    }
    const days = Number.isFinite(von) && Number.isFinite(bis)
        ? Math.round((bis - von) / 86_400_000) + 1
        : 0;
    return {
        entryCount: entries.length,
        billCount: rechnungen.size,
        days,
        totalCostCents,
        costPerDayCents: days > 0 ? Math.round(totalCostCents / days) : null,
        annualCostCents: days > 0 ? Math.round((totalCostCents * 365) / days) : null,
        perDivision: DIVISIONS.filter((division) => proSparte.has(division)).map((division) => ({
            division,
            costCents: proSparte.get(division)?.costCents ?? 0,
            quantity: proSparte.get(division)?.quantity ?? null,
            unit: DIVISION_UNITS[division],
        })),
        unit: einheitlich,
        quantity: menge,
        highQuantity: high,
        lowQuantity: low,
        pricePerUnitHundredths: menge && menge > 0 ? Math.round((totalCostCents * 100) / menge) : null,
        quantityPerDay: menge !== null && days > 0 ? menge / days : null,
        groupCents,
    };
}
/**
 * Zerlegt die Rechnungen in Spartenabschnitte und sortiert sie.
 *
 * Akontorechnungen stehen bewusst neben und nicht in der Auswertung: Sie sind
 * Vorauszahlungen auf denselben Verbrauch und würden jede Summe verdoppeln.
 * Für die Frage „habe ich zu viel oder zu wenig vorausbezahlt?" bleiben sie
 * greifbar.
 */
export function analyse(bills) {
    const sorted = [...bills].sort((a, b) => a.periodStart.localeCompare(b.periodStart));
    const entries = [];
    const akonto = [];
    const years = new Set();
    const divisions = new Set();
    for (const bill of sorted) {
        for (const section of bill.sections) {
            const entry = computeDivisionEntry(bill, section);
            if (bill.kind === 'akonto')
                akonto.push(entry);
            else
                entries.push(entry);
            divisions.add(section.division);
        }
        years.add(billYear(bill));
    }
    return {
        entries,
        akonto,
        years: [...years].sort((a, b) => b - a),
        divisions: DIVISIONS.filter((division) => divisions.has(division)),
    };
}
// ------------------------------------------------------------- Darstellung
/** „39.66" aus 3966 – der Ø Preis, wie er auf dem Bildschirm steht. */
export function formatRappen(hundredths) {
    if (hundredths === null)
        return '–';
    return (hundredths / 100).toLocaleString('de-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
/** „5'451" – Mengen mit Schweizer Tausendertrennung. */
export function formatMenge(value, fractionDigits = 0) {
    if (value === null)
        return '–';
    return value.toLocaleString('de-CH', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}
/**
 * Wie ein Preis je Einheit ausgeschrieben wird.
 *
 * Strom rechnet in Rappen je Kilowattstunde, Wasser in Franken je Kubikmeter –
 * so steht es auf der Rechnung, und so rechnet man auch im Kopf. „0.0125
 * Franken je Kilowattstunde" hat noch nie jemand gesagt.
 */
export function priceUnitLabel(unit) {
    if (unit === 'kWh')
        return { label: 'Rp./kWh', inRappen: true };
    if (unit === null)
        return { label: '', inRappen: false };
    return { label: `Fr./${unit}`, inRappen: false };
}
/** Der Preis je Einheit als Text, in der Schreibweise der Sparte. */
export function formatPricePerUnit(hundredths, unit) {
    if (hundredths === null)
        return '–';
    const { inRappen } = priceUnitLabel(unit);
    if (inRappen)
        return formatRappen(hundredths);
    return (hundredths / 10_000).toLocaleString('de-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
/** Der Zahlenwert des Preises in der Einheit, in der er angeschrieben wird. */
export function priceValue(hundredths, unit) {
    if (hundredths === null)
        return null;
    return priceUnitLabel(unit).inRappen ? hundredths / 100 : hundredths / 10_000;
}
/** „+5.2 %" / „−3.0 %" – mit echtem Minuszeichen, weil es besser trägt. */
export function formatPercentChange(ratio) {
    if (ratio === null)
        return '–';
    const percent = ratio * 100;
    const rounded = Math.abs(percent) < 10 ? percent.toFixed(1) : percent.toFixed(0);
    if (percent > 0)
        return `+${rounded} %`;
    return `${rounded.replace('-', '−')} %`;
}
/** „01.07.2024 – 31.12.2024" für Listen und Kopfzeilen. */
export function formatPeriod(periodStart, periodEnd) {
    return `${formatDate(periodStart)} – ${formatDate(periodEnd)}`;
}
export function formatDate(iso) {
    const [year, month, day] = iso.split('-');
    if (!year || !month || !day)
        return iso;
    return `${day}.${month}.${year}`;
}
/**
 * Kurzform einer Periode für die Achse eines Diagramms: „H2 24".
 *
 * Auf einem Handy ist unter einer Säule Platz für rund sechs Zeichen. Jahr,
 * Halbjahr und Quartal decken ab, wie dieser Versorger abrechnet; alles andere
 * fällt auf Monat und Jahr zurück, statt abgeschnitten zu werden.
 */
export function shortPeriodLabel(periodStart, periodEnd) {
    const start = new Date(`${periodStart}T00:00:00Z`);
    const end = new Date(`${periodEnd}T00:00:00Z`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
        return periodStart;
    const year = String(end.getUTCFullYear()).slice(2);
    const months = periodDays(periodStart, periodEnd) / 30.4;
    if (months >= 10)
        return `${end.getUTCFullYear()}`;
    if (months > 4.5)
        return `${start.getUTCMonth() < 6 ? 'H1' : 'H2'} ${year}`;
    if (months > 1.5)
        return `Q${Math.floor(start.getUTCMonth() / 3) + 1} ${year}`;
    return `${String(start.getUTCMonth() + 1).padStart(2, '0')}.${year}`;
}
//# sourceMappingURL=haus.js.map